import { useQuery } from '@apollo/client/react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import React from 'react';
import {
  BooleanField,
  BooleanInput,
  Button,
  DateField,
  Edit,
  EditButton,
  FilterList,
  FilterListItem,
  FilterLiveForm,
  Loading,
  SaveButton,
  SelectField,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useCreatePath,
  useRecordContext,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  AdminGetTrainingPlanFilterOptionsDocument,
  ExerciseUnit,
  TrainingPlanBlockDetailsFragment,
  TrainingPlanTemplateDetailsFragment,
  TrainingPlanWeekDetailsFragment,
} from '../../graphql/types';
import useCanWrite from '../../hooks/useCanWrite';
import useIsExerciseEditor from '../../hooks/useIsExerciseEditor';
import { IDSearch } from '../reusable/IDSearch';
import ClipboardField from './components/ClipboardField';
import InfiniteList from './components/InfiniteList';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import {
  isMaxUnit,
  transformTrainingPlanTemplate,
  WeekStructureInput,
  weeksToEditorWorkouts,
} from './TrainingPlanWeekStructureInput';

type FilterValue = string | number | null;
type FilterOptionRecord = Record<string, FilterValue>;
type FilterRecord = Record<string, FilterValue[] | undefined>;

export const attributeToChoice = (attr: {
  name: string;
  value?: FilterValue;
}) => ({
  id: attr.value,
  name: attr.name,
});

const handleMultiToggleFilter =
  (source: string) =>
  (value: FilterOptionRecord, filters: FilterRecord): FilterRecord => {
    const filterField = filters[source] || [];
    let filterValue = value[source];
    if (filterValue === 'null') {
      /**
       * react admin doesn't like null values in filters, so we use the string 'null' to represent it.
       * We need to convert it back to null here.
       */
      filterValue = null;
    }
    return {
      ...filters,
      [source]: filterField.some((v) => v === filterValue)
        ? // Remove the category if it was already present
          filterField.filter((v) => v !== filterValue)
        : // Add the category if it wasn't already present
          [...filterField, filterValue],
    };
  };

const isFilterSelected =
  (source: string) => (value: FilterOptionRecord, filters: FilterRecord) => {
    let filterValue = value[source];
    if (filterValue === 'null') {
      /**
       * react admin doesn't like null values in filters, so we use the string 'null' to represent it.
       * We need to convert it back to null here.
       */
      filterValue = null;
    }
    return Boolean(filters[source]?.includes(filterValue));
  };

const DenseFilterList = (props: React.ComponentProps<typeof FilterList>) => (
  <FilterList
    {...props}
    sx={{
      ...props.sx,
      marginBottom: 2,
      '& .MuiBox-root': {
        margin: 0,
      },
      '& .MuiBox-root > .MuiTypography-root': {
        marginBottom: 1,
        lineHeight: 1,
      },
    }}
  />
);
const DenseFilterListItem = (
  props: React.ComponentProps<typeof FilterListItem>,
) => (
  <FilterListItem
    {...props}
    sx={{
      ...props.sx,
      '& .RaFilterListItem-listItemButton': {
        paddingTop: 0,
        paddingBottom: 0,
      },
    }}
  />
);

const TrainingPlanTemplateFiltersAside = () => {
  const filterOptions = useQuery(AdminGetTrainingPlanFilterOptionsDocument);
  const {
    trainingPlanTypes = [],
    trainingPlanDifficulties = [],
    trainingPlanDurations = [],
    trainingPlanEquipmentCategories = [],
    trainingPlanFrequencies = [],
    trainingPlanPhases = [],
  } = filterOptions.data || {};

  return (
    <Card
      sx={{
        order: -1,
        mr: 2,
        mt: 7,
        flexShrink: 0,
        height: 'fit-content',
      }}
    >
      <CardContent sx={{ width: 225 }}>
        <FilterLiveForm>
          <IDSearch label="ID" source="id" />
        </FilterLiveForm>

        <DenseFilterList label="Type" icon={null}>
          {trainingPlanTypes.map((attribute) => (
            <DenseFilterListItem
              key={attribute.value}
              label={attribute.name}
              value={{ type: attribute.value }}
              isSelected={isFilterSelected('type')}
              toggleFilter={handleMultiToggleFilter('type')}
            />
          ))}
        </DenseFilterList>
        <DenseFilterList label="Level" icon={null}>
          {trainingPlanDifficulties.map((attribute) => (
            <DenseFilterListItem
              key={attribute.value}
              label={attribute.name}
              value={{ difficulty: attribute.value }}
              isSelected={isFilterSelected('difficulty')}
              toggleFilter={handleMultiToggleFilter('difficulty')}
            />
          ))}
        </DenseFilterList>
        <DenseFilterList label="Duration" icon={null}>
          {trainingPlanDurations.map((attribute) => (
            <DenseFilterListItem
              key={attribute.value}
              label={attribute.name}
              value={{ durationMinutes: attribute.value }}
              isSelected={isFilterSelected('durationMinutes')}
              toggleFilter={handleMultiToggleFilter('durationMinutes')}
            />
          ))}
        </DenseFilterList>
        <DenseFilterList label="Equipment Category" icon={null}>
          {trainingPlanEquipmentCategories.map((attribute) => (
            <DenseFilterListItem
              key={attribute.value}
              label={attribute.name}
              value={{ equipmentCategory: attribute.value ?? 'null' }}
              isSelected={isFilterSelected('equipmentCategory')}
              toggleFilter={handleMultiToggleFilter('equipmentCategory')}
            />
          ))}
        </DenseFilterList>
        <DenseFilterList label="Frequency" icon={null}>
          {trainingPlanFrequencies.map((attribute) => (
            <DenseFilterListItem
              key={attribute.value}
              label={attribute.name}
              value={{ frequency: attribute.value }}
              isSelected={isFilterSelected('frequency')}
              toggleFilter={handleMultiToggleFilter('frequency')}
            />
          ))}
        </DenseFilterList>
        <DenseFilterList label="Phase" icon={null}>
          {trainingPlanPhases.map((attribute) => (
            <DenseFilterListItem
              key={attribute.value}
              label={attribute.name}
              value={{ phase: attribute.value }}
              isSelected={isFilterSelected('phase')}
              toggleFilter={handleMultiToggleFilter('phase')}
            />
          ))}
        </DenseFilterList>

        <DenseFilterList label="Other" icon={null}>
          <DenseFilterListItem
            label={'Archived'}
            value={{ isArchived: true }}
          />
          <DenseFilterListItem label={'Deleted'} value={{ isDeleted: true }} />
        </DenseFilterList>
      </CardContent>
    </Card>
  );
};

export const TrainingPlanTemplateList: React.FC = () => {
  const filterOptions = useQuery(AdminGetTrainingPlanFilterOptionsDocument);
  const {
    trainingPlanDifficulties = [],
    trainingPlanDurations = [],
    trainingPlanEquipmentCategories = [],
    trainingPlanFrequencies = [],
    trainingPlanPhases = [],
  } = filterOptions.data || {};

  const isExerciseEditor = useIsExerciseEditor();
  const canWrite = useCanWrite();
  const canModifyExercise = canWrite || isExerciseEditor;

  return (
    <InfiniteList aside={<TrainingPlanTemplateFiltersAside />}>
      <WriteOnlyDatagrid bulkActionButtons={false}>
        <ClipboardField truncate={8} source="id" sortable={false} />
        <TextField source="name" label="Name" sortable={false} />
        <TextField source="type.name" label="Type" sortable={false} />
        <SelectField
          source="difficulty"
          choices={trainingPlanDifficulties.map(attributeToChoice)}
          sortable={false}
        />
        <SelectField
          source="durationMinutes"
          label="Duration (min)"
          choices={trainingPlanDurations.map(attributeToChoice)}
          sortable={false}
        />
        <SelectField
          source="equipmentCategory"
          label="Equipment Category"
          choices={trainingPlanEquipmentCategories.map(attributeToChoice)}
          sortable={false}
        />
        <SelectField
          source="frequency"
          choices={trainingPlanFrequencies.map(attributeToChoice)}
          sortable={false}
        />
        <SelectField
          source="phase"
          choices={trainingPlanPhases.map(attributeToChoice)}
          sortable={false}
        />
        <BooleanField source="hidden" label="Hidden" sortable={false} />
        <ShowButton />
        {canModifyExercise && <EditButton />}
      </WriteOnlyDatagrid>
    </InfiniteList>
  );
};

const sortByOrder = <T extends { order: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.order - b.order);

/** Read-only summary of a single exercise, e.g. "10 Reps" or "Max Reps". */
const formatExerciseCount = (
  count: number,
  unit: { name: string; value: ExerciseUnit },
): string => (isMaxUnit(unit.value) ? unit.name : `${count} ${unit.name}`);

/**
 * Compact, read-only view of a block: its training component, set count and
 * the exercises in each set. Sets are identical by construction, so only the
 * first set's exercises are listed.
 */
const BlockPreview: React.FC<{
  block: TrainingPlanBlockDetailsFragment;
  blockIndex: number;
}> = ({ block, blockIndex }) => {
  const sets = sortByOrder(block.sets.edges.map(({ node }) => node));
  const exercises = sortByOrder(
    (sets[0]?.trainingPlanExercises.edges ?? []).map(({ node }) => node),
  );

  return (
    <Box>
      <Typography variant="body2" fontWeight="medium">
        {blockIndex + 1}. {block.trainingComponent.name}
        <Typography component="span" variant="caption" color="text.secondary">
          {' '}
          · {sets.length} {sets.length === 1 ? 'set' : 'sets'}
        </Typography>
      </Typography>
      <Stack component="ul" sx={{ m: 0, pl: 3 }} spacing={0.25}>
        {exercises.map((exercise) => (
          <Typography
            key={exercise.id}
            component="li"
            variant="body2"
            color="text.secondary"
          >
            {exercise.exercise.displayName || exercise.exercise.name}
            {' — '}
            {formatExerciseCount(exercise.count, exercise.unit)}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

const WeeksDisplay: React.FC = () => {
  const record = useRecordContext<{
    weeks?: { edges: { node: TrainingPlanWeekDetailsFragment }[] };
  }>();

  const firstWeek = sortByOrder(
    (record?.weeks?.edges ?? []).map(({ node }) => node),
  )[0];

  if (!firstWeek) {
    return <span style={{ color: '#666' }}>No weeks</span>;
  }

  const workouts = sortByOrder(
    firstWeek.workouts.edges.map(({ node }) => node),
  );

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="h5" gutterBottom>
        Week Preview
      </Typography>
      <Stack spacing={1.5}>
        {workouts.map((workout, workoutIndex) => (
          <Card key={workout.id} variant="outlined">
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" gutterBottom>
                Workout {workoutIndex + 1}
              </Typography>
              <Stack spacing={1}>
                {sortByOrder(workout.blocks.edges.map(({ node }) => node)).map(
                  (block, blockIndex) => (
                    <BlockPreview
                      key={block.id}
                      block={block}
                      blockIndex={blockIndex}
                    />
                  ),
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

const buildDuplicateRecord = (record: TrainingPlanTemplateDetailsFragment) => ({
  name: record.name,
  hidden: true,
  type: record.type?.value,
  difficulty: record.difficulty,
  durationMinutes: record.durationMinutes,
  equipmentCategory: record.equipmentCategory,
  frequency: record.frequency,
  phase: record.phase,
  workouts: weeksToEditorWorkouts(record.weeks),
});

/**
 * Opens the create screen prefilled with a copy of the current plan, including
 * its full week/workout/block/set/exercise structure.
 */
const DuplicateButton: React.FC = () => {
  const record = useRecordContext<TrainingPlanTemplateDetailsFragment>();
  const navigate = useNavigate();
  const createPath = useCreatePath();

  if (!record?.weeks) {
    return null;
  }

  const handleClick = () => {
    navigate(createPath({ resource: 'TrainingPlan', type: 'create' }), {
      state: { record: buildDuplicateRecord(record) },
    });
  };

  return (
    <Button label="Duplicate" onClick={handleClick}>
      <ContentCopyIcon />
    </Button>
  );
};

const TrainingPlanTemplateShowActions: React.FC = () => {
  const isExerciseEditor = useIsExerciseEditor();
  const canWrite = useCanWrite();
  const canModifyExercise = canWrite || isExerciseEditor;

  if (!canModifyExercise) {
    return null;
  }

  return (
    <TopToolbar>
      <EditButton />
      <DuplicateButton />
    </TopToolbar>
  );
};

export const TrainingPlanTemplateShow: React.FC = () => {
  const filterOptions = useQuery(AdminGetTrainingPlanFilterOptionsDocument);
  const {
    trainingPlanDifficulties = [],
    trainingPlanDurations = [],
    trainingPlanEquipmentCategories = [],
    trainingPlanFrequencies = [],
    trainingPlanPhases = [],
  } = filterOptions.data || {};

  return (
    <Show actions={<TrainingPlanTemplateShowActions />}>
      <SimpleShowLayout>
        <ClipboardField source="id" label="ID" />
        <TextField source="name" />
        <BooleanField source="hidden" label="Hidden" />
        <TextField source="type.name" label="Type" />
        <SelectField
          source="difficulty"
          choices={trainingPlanDifficulties.map(attributeToChoice)}
        />
        <SelectField
          source="durationMinutes"
          label="Duration (min)"
          choices={trainingPlanDurations.map(attributeToChoice)}
        />
        <SelectField
          source="equipmentCategory"
          label="Equipment Category"
          choices={trainingPlanEquipmentCategories.map(attributeToChoice)}
        />
        <SelectField
          source="frequency"
          choices={trainingPlanFrequencies.map(attributeToChoice)}
        />
        <SelectField
          source="phase"
          choices={trainingPlanPhases.map(attributeToChoice)}
        />
        <DateField source="createdAt" showTime label="Created" />
        <DateField source="archivedAt" showTime label="Archived" />
        <DateField source="deletedAt" showTime label="Deleted" />
        <WeeksDisplay />
      </SimpleShowLayout>
    </Show>
  );
};

const EditToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

/** Seeds the week-structure editor from the template's existing first week. */
const EditWeekStructureInput: React.FC = () => {
  const record = useRecordContext<TrainingPlanTemplateDetailsFragment>();
  const defaultWorkouts = React.useMemo(
    () => (record?.weeks ? weeksToEditorWorkouts(record.weeks) : undefined),
    [record],
  );

  // Forcing a rerender via key after saving changes to the workouts, otherwise stale values display
  const seedKey = React.useMemo(
    () => JSON.stringify(defaultWorkouts ?? null),
    [defaultWorkouts],
  );

  return (
    <WeekStructureInput
      key={seedKey}
      source="workouts"
      defaultValue={defaultWorkouts}
    />
  );
};

/**
 * The list view seeds react-admin's cache with a record that has no `weeks`,
 * so navigating list -> edit would mount the form before the weeks are loaded
 * and the week-structure editor would initialize empty, so we specifically fetch it first
 */
const TrainingPlanTemplateEditForm: React.FC = () => {
  const filterOptions = useQuery(AdminGetTrainingPlanFilterOptionsDocument);
  const {
    trainingPlanTypes = [],
    trainingPlanDifficulties = [],
    trainingPlanDurations = [],
    trainingPlanEquipmentCategories = [],
    trainingPlanFrequencies = [],
    trainingPlanPhases = [],
  } = filterOptions.data || {};

  const record = useRecordContext<TrainingPlanTemplateDetailsFragment>();

  if (!record?.weeks) {
    return <Loading />;
  }

  return (
    <SimpleForm toolbar={<EditToolbar />}>
      <TextInput source="id" InputProps={{ disabled: true }} />
      <TextInput source="name" fullWidth />
      <BooleanInput source="hidden" />
      <SelectInput
        source="type.value"
        label="Type"
        choices={trainingPlanTypes.map(attributeToChoice)}
      />
      <SelectInput
        source="difficulty"
        choices={trainingPlanDifficulties.map(attributeToChoice)}
      />
      <SelectInput
        source="durationMinutes"
        label="Duration (min)"
        choices={trainingPlanDurations.map(attributeToChoice)}
      />
      <SelectInput
        source="equipmentCategory"
        label="Equipment Category"
        choices={trainingPlanEquipmentCategories.map(attributeToChoice)}
      />
      <SelectInput
        source="frequency"
        choices={trainingPlanFrequencies.map(attributeToChoice)}
      />
      <SelectInput
        source="phase"
        choices={trainingPlanPhases.map(attributeToChoice)}
      />
      <EditWeekStructureInput />
    </SimpleForm>
  );
};

export const TrainingPlanTemplateEdit: React.FC = () => (
  <Edit
    redirect={false}
    mutationMode="pessimistic"
    transform={transformTrainingPlanTemplate}
  >
    <TrainingPlanTemplateEditForm />
  </Edit>
);

export { DirectionsRunIcon as TrainingPlanTemplateIcon };
